import { localApiUrl, mainApiUrl, baseHeaders, currencies } from '@constants'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getStateFromLocalStorage, storeStateAtLocalStorage } from '@src/reduxjs'
import { getRandomId } from '@src/utils'
import pRetry from 'p-retry'
import ms from 'ms'

const initialState = {
  items: [],
  addOns: [],
  loadedCart: {
    id: '',
    token: null,
    ...currencies.reduce((acc, { totalKeyLowerCase }) => ({ ...acc, [totalKeyLowerCase]: null }), {}),
  },
  lastLoadedCartId: '',
  couponApplied: false,
  couponMessage: '',
  discountCode: '',
  pixData: {},
  paymentMethod: null,
  paymentRedirectStatus: '',
  isRedirecting: null,
  mercadoPagoError: false,
  paypalError: false,
  notes: '',
  freeItems: [],
  order: {
    data: {},
    error: false,
    rejectMessage: '',
    paymentStatusDetail: '',
  },
  user: {
    email: '',
    phoneNumber: '',
    deliveryAddress: {
      id: '',
      line1: '',
      line2: '',
      postalCode: '',
      locality: '',
      country: '',
      reference: '',
    },
  },
  paymentData: currencies.reduce((acc, { totalKey, originalTotalKey }) => ({ ...acc, [totalKey]: null, [originalTotalKey]: null }), {}),
  addOnsPaymentData: currencies.reduce((acc, { totalKey }) => ({ ...acc, [totalKey]: null }), {}),
  inProgress: false,
}

export const createOrderThunk = createAsyncThunk('cart/createOrderThunk', async (data, { getState, rejectWithValue }) => {
  const body = JSON.stringify(data)
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }

  const response = await fetch(`${mainApiUrl}/orders`, { signal: AbortSignal.timeout(ms('30s')), body, headers, method: 'POST' })
  const json = await response.json()

  if (response.status !== 201) {
    return rejectWithValue({ ...json, status: response.status, isStore: getState().main.isStore })
  }

  return json
})

export const pingOrderThunk = createAsyncThunk('cart/pingOrderThunk', async (order, { getState, rejectWithValue }) => {
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }

  const response = await fetch(`${mainApiUrl}/orders/${order.id}`, { signal: AbortSignal.timeout(ms('15s')), headers, method: 'GET' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

export const createFormThunk = createAsyncThunk('cart/createFormThunk', async (data, { getState, rejectWithValue }) => {
  const body = JSON.stringify(data)
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }

  const response = await fetch(`${mainApiUrl}/orders`, { signal: AbortSignal.timeout(ms('30s')), body, headers, method: 'POST' })
  const json = await response.json()

  if (response.status !== 201) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

export const requestImageUploadThunk = createAsyncThunk('cart/requestImageUploadThunk', async ({ id }, { getState, rejectWithValue }) => {
  const doFetch = async () => {
    return await fetch(`${localApiUrl}/images/${id}/upload`, { signal: AbortSignal.timeout(ms('1m')), body: JSON.stringify({}), headers: baseHeaders, method: 'PUT' })
  }

  const response = await pRetry(doFetch, { retries: 3 })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return { ...json, status: response.status }
})

export const refreshItemThunk = createAsyncThunk('cart/refreshItemThunk', async (data, { getState, rejectWithValue }) => {
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/photos/${data.id}`, { headers, method: 'GET' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

export const validateDiscountCodeThunk = createAsyncThunk('cart/validateDiscountCodeThunk', async (data, { getState, rejectWithValue }) => {
  const body = JSON.stringify(data)
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/coupons`, { body, headers, method: 'PUT' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

export const saveCartThunk = createAsyncThunk('cart/saveCartThunk', async (data, { getState, rejectWithValue }) => {
  const body = JSON.stringify(data)
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/carts`, { body, headers, method: 'PUT' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

export const loadCartThunk = createAsyncThunk('cart/loadCartThunk', async ({ code, store, is_store }, { getState, rejectWithValue }) => {
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/carts/${code}?store=${store}&is_store=${is_store ? 'true' : 'false'}`, { headers, method: 'GET' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

const cartSlice = createSlice({
  name: 'cart',
  initialState: { ...initialState, ...getStateFromLocalStorage('cartSlice') },
  reducers: {
    addItem: (state, action) => {
      state.items = state.items.filter(i => i && i.id)
      if (action.payload && !state.items.some((i) => i.id === action.payload.id)) {
        state.items.push(action.payload)
        state.loadedCart = initialState.loadedCart
        storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
      }
    },
    toggleItem: (state, { payload }) => {
      state.items = state.items.filter(i => i && i.id)

      if (!payload) {
        return state
      }

      state.loadedCart = initialState.loadedCart

      if (state.items.some((i) => i.id === payload.id)) {
        const idToDelete = payload.id
        state.items = state.items.filter((i) => i.id !== idToDelete)
        state.addOns = state.addOns.filter((i) => i.item_id !== idToDelete)

        storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })

        return state
      }

      state.items.push(payload)
      storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })

      return state
    },
    sortItems: (state, action) => {
      state.items = state.items.filter(i => i && i.id)
      state.items = state.items.sort((a, b) => {
        return new Date(a.taken_at) - new Date(b.taken_at)
      }).sort((a, b) => {
        return a.liked ? -1 : 1
      })
    },
    deleteItem: (state, { payload }) => {
      state.items = state.items.filter(i => i && i.id)
      const idToDelete = payload.id
      state.items = state.items.filter((i) => i.id !== idToDelete)
      state.addOns = state.addOns.filter((i) => i.item_id !== idToDelete)
      state.loadedCart = initialState.loadedCart

      storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
    },
    setAddOns: (state, action) => {
      state.addOns = (action.payload || []).sort((a, b) => a.addOn.name.localeCompare(b.addOn.name))
      state.loadedCart = initialState.loadedCart
    },
    emptyCart: (state) => {
      state.items = initialState.items
      state.addOns = initialState.addOns
      state.loadedCart = initialState.loadedCart
      storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
    },
    clearOrder: (state) => {
      state.order = initialState.order
    },
    clearCouponMessage: (state) => {
      state.couponMessage = initialState.couponMessage
    },
    setOrderError: (state, action) => {
      state.order.error = action.payload || false
    },
    setKey: (state, action) => {
      const { key, value } = action.payload

      return { ...state, [key]: value }
    },
    setUser: (state, action) => {
      state.user = action.payload || initialState.user
    },
    setUserKey: (state, action) => {
      const { key, value } = action.payload

      state.user = { ...state.user, [key]: value }
    },
    setPaymentData: (state, action) => {
      state.paymentData = action.payload || initialState.paymentData
    },
    setPaymentDataKey: (state, action) => {
      const { key, value } = action.payload

      state.paymentData = { ...state.paymentData, [key]: value }
    },
    setAddOnsPaymentData: (state, action) => {
      state.addOnsPaymentData = action.payload || initialState.addOnsPaymentData
    },
    toggleItemLike: (state, action) => {
      state.items = state.items.map(i => {
        if (i.id === action.payload.id) {
          i.liked = !i.liked
        }

        return i
      })

      storeStateAtLocalStorage('cartSlice', { items: state.items })

      return state
    },
    removeNonLiked: (state) => {
      const itemsToRemove = state.items.filter(i => !i || !i.id || !i.liked)
      const itemShouldBeIncluded = (i) => itemsToRemove.every(item => item.id !== i.id)

      state.items = state.items.filter(itemShouldBeIncluded)
      state.addOns = state.addOns.filter(({ items }) => {
        if (items.every(i => !itemShouldBeIncluded({ id: i }))) {
          return false
        }

        return true
      }).map(addOn => {
        return { ...addOn, items: addOn.items.filter(itemShouldBeIncluded) }
      })

      storeStateAtLocalStorage('cartSlice', { items: state.items })
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(({ type }) => type === `${createOrderThunk.pending}`, (state) => {
      state.inProgress = true
      state.order.error = false
      state.order.data = {}
      state.order.rejectMessage = ''
      state.order.paymentStatusDetail = ''
    })
    .addMatcher(({ type }) => type === `${createOrderThunk.fulfilled}`, (state, action) => {
      const { data } = action.payload
      if (data && data.order && (['approved', 'completed'].includes(data.order.payment_status) || ['cash', 'bank_transfer'].includes(data.order.payment_method))) {
        state.items = initialState.items
        state.addOns = initialState.addOns
        state.loadedCart = initialState.loadedCart
        state.lastLoadedCartId = initialState.lastLoadedCartId
        storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
      }
      state.order.data = action.payload.data.order
      state.order.paymentStatusDetail = action.payload.data.payment_status_detail || ''
      state.order.rejectMessage = ''
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${createOrderThunk.rejected}`, (state, action) => {
      if (action.payload && action.payload.clear_cart) {
        state.items = initialState.items
        state.addOns = initialState.addOns
        state.loadedCart = initialState.loadedCart
        state.lastLoadedCartId = initialState.lastLoadedCartId
        storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
      }
      state.order.rejectMessage = (action.payload || {}).message  || ''
      state.order.error = true
      state.order.data = {}
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${pingOrderThunk.fulfilled}`, (state, action) => {
      const { data } = action.payload
      if (data && ['approved', 'completed'].includes(data.payment_status)) {
        state.items = initialState.items
        state.addOns = initialState.addOns
        state.loadedCart = initialState.loadedCart
        state.lastLoadedCartId = initialState.lastLoadedCartId
        storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
      }
      state.order.data = action.payload.data
    })
    .addMatcher(({ type }) => type === `${createFormThunk.pending}`, (state) => {
      state.order.error = false
      state.order.data = {}
      state.inProgress = true
      state.order.rejectMessage = ''
      state.order.paymentStatusDetail = ''
    })
    .addMatcher(({ type }) => type === `${createFormThunk.fulfilled}`, (state, action) => {
      state.order.data = {}
      state.order.paymentStatusDetail = ''
      state.inProgress = false
      state.order.rejectMessage = ''
    })
    .addMatcher(({ type }) => type === `${createFormThunk.rejected}`, (state, action) => {
      state.order.error = true
      state.order.data = {}
      state.inProgress = false
      state.order.rejectMessage = (action.payload || {}).message  || ''
    })
    .addMatcher(({ type }) => type === `${requestImageUploadThunk.pending}`, (state) => {
      state.order.error = false
      state.order.data = {}
      state.inProgress = true
    })
    .addMatcher(({ type }) => type === `${requestImageUploadThunk.fulfilled}`, (state, action) => {
      const { data } = action.payload

      if (data && data.s3_key_base62) {
        const index = state.items.findIndex((i) => i.id === data.id)
        if (index >= 0) {
          state.items[index].s3_key_base62 = data.s3_key_base62
          storeStateAtLocalStorage('cartSlice', { items: state.items })
        }
      }

      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${requestImageUploadThunk.rejected}`, (state, action) => {
      state.order.error = true
      state.order.data = {}
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${refreshItemThunk.rejected}`, (state, action) => {
      const { arg } = action.meta

      if (action.payload && action.payload.status === 404) {
        state.items = state.items.filter((i) => i.id !== arg.id)
        state.addOns = state.addOns.filter(({ item_id: id }) => state.items.find(i => i.id === id))
        state.loadedCart = initialState.loadedCart
        state.lastLoadedCartId = initialState.lastLoadedCartId

        storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
      }
    })
    .addMatcher(({ type }) => type === `${refreshItemThunk.fulfilled}`, (state, action) => {
      const { data } = action.payload

      if (data) {
        const index = state.items.findIndex(({ id }) => id === data.id)

        if (index >= 0) {
          state.items[index] = { ...state.items[index], ...data }

          storeStateAtLocalStorage('cartSlice', { items: state.items })
        }
      }
    })
    .addMatcher(({ type }) => type === `${loadCartThunk.pending}`, (state) => {
      state.inProgress = true
    })
    .addMatcher(({ type }) => type === `${loadCartThunk.fulfilled}`, (state, action) => {
      const { delivery_email: deliveryEmail, phone_number: phoneNumber, ...data } = action.payload.data

      state.loadedCart = data
      state.lastLoadedCartId = data.id
      state.items = (data.items || []).map(i => ({ ...i, liked: (data.liked_items_ids || []).includes(i.id) }))
      state.addOns = []
      state.user.email = state.user.email || deliveryEmail
      state.user.phoneNumber = state.user.phoneNumber || phoneNumber
      state.inProgress = false

      storeStateAtLocalStorage('cartSlice', { items: state.items, loadedCart: state.loadedCart })
    })
    .addMatcher(({ type }) => type === `${loadCartThunk.rejected}`, (state, { payload }) => {
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${saveCartThunk.pending}`, (state) => {
      state.inProgress = true
      state.couponMessage = ''
    })
    .addMatcher(({ type }) => type === `${saveCartThunk.rejected}`, (state, { payload }) => {
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${saveCartThunk.fulfilled}`, (state, { payload }) => {
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${validateDiscountCodeThunk.pending}`, (state) => {
      state.inProgress = true
      state.couponMessage = ''
    })
    .addMatcher(({ type }) => type === `${validateDiscountCodeThunk.rejected}`, (state, { payload }) => {
      state.inProgress = false
      state.couponMessage = (payload || {}).message || 'Error!'
      state.discountCode = ''
    })
    .addMatcher(({ type }) => type === `${validateDiscountCodeThunk.fulfilled}`, (state, { payload }) => {
      state.inProgress = false
      const { free_items: freeItems, items } = payload

      for (const { originalTotalKeyLowerCase, originalTotalKey, totalKeyLowerCase, totalKey } of currencies) {
        state.paymentData[originalTotalKey] = payload[originalTotalKeyLowerCase]
        state.paymentData[totalKey] = payload[totalKeyLowerCase]
      }

      state.freeItems = freeItems
      state.items = items
      state.addOns = initialState.addOns
      state.couponApplied = true
      state.loadedCart = initialState.loadedCart
      state.lastLoadedCartId = initialState.lastLoadedCartId
    })
  },
})

export const { addItem, toggleItem, setAddOns, deleteItem, emptyCart, clearOrder, clearCouponMessage, setOrderError, removeNonLiked, toggleItemLike, sortItems, setKey, setPaymentData, setPaymentDataKey, setUser, setUserKey, setAddOnsPaymentData } = cartSlice.actions

export default cartSlice

